// src/components/dashboard/RoadmapOverview.jsx
import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Badge } from "components/ui/badge";
import { Progress } from "components/ui/progress";
import { Map as MapIcon, Clock, Target } from "lucide-react";
import { Skeleton } from "components/ui/skeleton";

export default function RoadmapOverview({ roadmapItems = [], isLoading }) {
  const timelineStats = useMemo(() => {
    const now = roadmapItems.filter((i) => i.time_horizon === "Now").length;
    const next = roadmapItems.filter((i) => i.time_horizon === "Next").length;
    const later = roadmapItems.filter((i) => i.time_horizon === "Later").length;
    return { now, next, later };
  }, [roadmapItems]);

  const statusStats = useMemo(() => {
    const map = {};
    for (const item of roadmapItems) {
      map[item.status] = (map[item.status] || 0) + 1;
    }
    return map;
  }, [roadmapItems]);

  const STATUS_ORDER = [
    "planned",
    "in_progress",
    "done",
    "in_discovery",
    "in_development",
    "testing",
    "launched",
    "measuring",
  ];

  const statusPalette = {
    planned: { bg: "var(--bg-subtle)", color: "var(--fg-secondary)" }, // slate
    in_progress: { bg: "var(--warning-soft)", color: "var(--warning-fg)" }, // orange
    done: { bg: "var(--success-soft)", color: "var(--success-fg)" }, // green
    in_discovery: { bg: "var(--accent-soft)", color: "var(--accent-fg)" }, // blue
    in_development: { bg: "var(--warning-soft)", color: "var(--warning-fg)" }, // orange
    testing: { bg: "var(--accent-soft)", color: "var(--accent-fg)" }, // purple
    launched: { bg: "var(--success-soft)", color: "var(--success-fg)" }, // green
    measuring: { bg: "var(--success-soft)", color: "var(--success-fg)" }, // emerald
  };

  const toEntriesOrdered = (stats) => {
    const entries = Object.entries(stats);
    return entries.sort((a, b) => {
      const ia = STATUS_ORDER.indexOf(a[0]);
      const ib = STATUS_ORDER.indexOf(b[0]);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });
  };

  const recent = useMemo(() => {
    return [...roadmapItems]
      .sort((a, b) => new Date(b.updated_date || 0) - new Date(a.updated_date || 0))
      .slice(0, 3);
  }, [roadmapItems]);

  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--fg)" }}>
          <MapIcon size={20} color="var(--accent-fg)" />
          Roadmap Overview
        </CardTitle>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <div style={{ display: "grid", gap: 16 }}>
            <Skeleton style={{ height: 128, width: "100%", borderRadius: 12 }} />
            <Skeleton style={{ height: 96, width: "100%", borderRadius: 12 }} />
          </div>
        ) : roadmapItems.length > 0 ? (
          <div style={{ display: "grid", gap: 24 }}>
            {/* Timeline Distribution */}
            <div>
              <h4
                style={{
                  margin: 0,
                  marginBottom: 12,
                  fontSize: 14,
                  fontWeight: 700,
                  color: "var(--fg)",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Clock size={16} />
                Timeline Distribution
              </h4>
              <div
                className="grid"
                style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(3, minmax(0,1fr))" }}
              >
                <div
                  style={{
                    padding: 16,
                    borderRadius: 12,
                    border: "1px solid var(--border)",
                    background: "var(--success-soft)",
                  }}
                >
                  <div style={{ fontSize: 24, fontWeight: 800, color: "var(--success-fg)", marginBottom: 4 }}>
                    {timelineStats.now}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--success-fg)" }}>Now</div>
                  <div style={{ fontSize: 12, color: "var(--success-fg)" }}>Current sprint</div>
                </div>

                <div
                  style={{
                    padding: 16,
                    borderRadius: 12,
                    border: "1px solid var(--border)",
                    background: "var(--accent-soft)",
                  }}
                >
                  <div style={{ fontSize: 24, fontWeight: 800, color: "var(--accent-fg)", marginBottom: 4 }}>
                    {timelineStats.next}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--accent-fg)" }}>Next</div>
                  <div style={{ fontSize: 12, color: "var(--accent-fg)" }}>Coming up</div>
                </div>

                <div
                  style={{
                    padding: 16,
                    borderRadius: 12,
                    border: "1px solid var(--border)",
                    background: "var(--bg-subtle)",
                  }}
                >
                  <div style={{ fontSize: 24, fontWeight: 800, color: "var(--fg-secondary)", marginBottom: 4 }}>
                    {timelineStats.later}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--fg-secondary)" }}>Later</div>
                  <div style={{ fontSize: 12, color: "var(--fg-muted)" }}>Future work</div>
                </div>
              </div>
            </div>

            {/* Status Overview */}
            <div>
              <h4
                style={{
                  margin: 0,
                  marginBottom: 12,
                  fontSize: 14,
                  fontWeight: 700,
                  color: "var(--fg)",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Target size={16} />
                Initiative Status
              </h4>

              <div style={{ display: "grid", gap: 8 }}>
                {toEntriesOrdered(statusStats).map(([status, count]) => {
                  const pal = statusPalette[status] || { bg: "var(--bg-subtle)", color: "var(--fg-secondary)" };
                  return (
                    <div
                      key={status}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "6px 0",
                      }}
                    >
                      <Badge
                        className="text-xs"
                        style={{
                          background: pal.bg,
                          color: pal.color,
                          border: 0,
                          fontSize: 12,
                          textTransform: "capitalize",
                        }}
                      >
                        {String(status || "").replace(/_/g, " ")}
                      </Badge>
                      <span style={{ fontWeight: 700, color: "var(--fg-secondary)" }}>{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recent Updates */}
            <div>
              <h4 style={{ margin: 0, marginBottom: 12, fontSize: 14, fontWeight: 700, color: "var(--fg)" }}>
                Recent Updates
              </h4>
              <div style={{ display: "grid", gap: 10 }}>
                {recent.map((item) => {
                  const pal = statusPalette[item.status] || { bg: "var(--bg-subtle)", color: "var(--fg-secondary)" };
                  return (
                    <div
                      key={item.id}
                      style={{
                        padding: 12,
                        borderRadius: 10,
                        border: "1px solid var(--border)",
                        background: "var(--bg-subtle)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "start",
                          justifyContent: "space-between",
                          gap: 8,
                          marginBottom: 6,
                        }}
                      >
                        <h5
                          style={{
                            margin: 0,
                            fontSize: 14,
                            fontWeight: 600,
                            color: "var(--fg)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={item.title}
                        >
                          {item.title}
                        </h5>
                        <Badge
                          className="text-xs"
                          style={{ background: pal.bg, color: pal.color, border: 0, fontSize: 12 }}
                        >
                          {String(item.status || "").replace(/_/g, " ")}
                        </Badge>
                      </div>

                      {item.description && (
                        <p
                          style={{
                            margin: 0,
                            fontSize: 12,
                            color: "var(--fg-muted)",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                          title={item.description}
                        >
                          {item.description}
                        </p>
                      )}

                      {Number(item.learning_score) > 0 && (
                        <div style={{ marginTop: 10 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              fontSize: 12,
                              color: "var(--fg-muted)",
                              marginBottom: 6,
                            }}
                          >
                            <span>Learning Score</span>
                            <span>{Number(item.learning_score)}%</span>
                          </div>
                          <Progress value={Number(item.learning_score)} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "36px 0" }}>
            <MapIcon size={48} color="var(--border-strong)" style={{ marginBottom: 8 }} />
            <p style={{ margin: 0, color: "var(--fg-muted)" }}>No roadmap items yet</p>
            <p style={{ margin: 0, marginTop: 4, fontSize: 12, color: "var(--fg-subtle)" }}>
              Create initiatives to start building your roadmap
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
